import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  Input,
  OnDestroy,
  QueryList,
  ViewEncapsulation,
} from '@angular/core';
import { FocusKeyManager } from '@angular/cdk/a11y';
import { Subscription } from 'rxjs';

import { PhxOverviewItemComponent } from './phx-overview-item.component';
import { PhxOverviewPrefixComponent } from './phx-overview-prefix.component';
import { KeyboardKeys } from '../../helpers/KeyboardKeys.enum';
import { PHX_BACKGROUND, PHX_SIZE } from '../../constants';
import { PhxOverviewBackground } from './phx-overview.types';
import { PhxBreakpointObserverService } from '../../services/phx-breakpoint-service';
import { PhxContextService, PhxSizeMapType } from '../../services/phx-context-service';
import { PhxContainerComponentService } from '../../services/phx-container-component.service';


const DESKTOP_SIZE_MAP: PhxSizeMapType = {
  'phx-select': PHX_SIZE.SM,
  'phx-radio-list': PHX_SIZE.XS,
  'phx-navigation-button': PHX_SIZE.SM,
};

const MOBILE_SIZE_MAP: PhxSizeMapType = {
  'phx-select': PHX_SIZE.MD,
  'phx-radio-list': PHX_SIZE.MD,
  'phx-navigation-button': PHX_SIZE.MD,
};

@Component({
  selector: 'phx-overview',
  styleUrls: ['./phx-overview.component.scss'],
  templateUrl: './phx-overview.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'phx-overview',
    '[class.phx-overview-bg-standard]': 'phxBackground === PHX_BACKGROUND.STANDARD',
    '[class.phx-overview-loading]': 'phxLoading',
  },
  providers: [{provide: PhxContextService, useClass: PhxContainerComponentService}]
})
export class PhxOverviewComponent implements AfterContentInit, OnDestroy {
  @Input() phxBackground: PhxOverviewBackground = PHX_BACKGROUND.DARK;
  @Input() phxLoading: boolean = false;

  @ContentChildren(PhxOverviewItemComponent) private _items!: QueryList<PhxOverviewItemComponent>;
  @ContentChildren(PhxOverviewPrefixComponent) private _prefixItems!: QueryList<PhxOverviewPrefixComponent>;

  private _keyManager!: FocusKeyManager<PhxOverviewItemComponent>;
  private _itemsSubscription!: Subscription;
  private _breakpointObserverSubscription!: Subscription;

  public isMobileView: boolean = false;
  public loadingItemsArray: number[] = [];
  public readonly PHX_BACKGROUND = PHX_BACKGROUND;

  public get hasPrefixContent(): boolean {
    return (this._prefixItems?.length ?? 0) > 0;
  }

  constructor(private _phxBreakpointObserver: PhxBreakpointObserverService, private _cdr: ChangeDetectorRef, private _contextService: PhxContextService) {
    const containerService = this._contextService as PhxContainerComponentService;
    this._breakpointObserverSubscription = this._phxBreakpointObserver.smMobile.subscribe(isMobile => {
      this.isMobileView = isMobile;
      containerService.updateSizeMap(isMobile ? MOBILE_SIZE_MAP : DESKTOP_SIZE_MAP);
      this._cdr.markForCheck();
    });
  }
  
  public ngAfterContentInit(): void {
    this._itemsSubscription = this._items.changes.subscribe(() => {
      this._keyManager = new FocusKeyManager(this._items).skipPredicate((item: PhxOverviewItemComponent) => item.phxDisabled || !item.phxClickable).withWrap();
    });
    this._items.notifyOnChanges();
    this.loadingItemsArray = Array(this._items.length ?? 0).fill(0);
  }

  public ngOnDestroy(): void {
    this._itemsSubscription.unsubscribe();
    this._breakpointObserverSubscription.unsubscribe();
  }

  /** Called when the body container receives focus (via Tab or programmatic focus). */
  public onBodyFocus(): void {
    this._keyManager?.setFirstItemActive();
  }

  public onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case KeyboardKeys.ARROW_DOWN:
      case KeyboardKeys.ARROW_LEFT:
        event.preventDefault();
        this._keyManager.setPreviousItemActive();
        break;
      case KeyboardKeys.ARROW_UP:
      case KeyboardKeys.ARROW_RIGHT:
        event.preventDefault();
        this._keyManager.setNextItemActive();
        break;
      case KeyboardKeys.TAB:
        this._keyManager.setActiveItem(-1);
        break;
      default:
        break;
    }
  }
}
